import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useTranslation } from 'react-i18next'

type DialogType = 'confirm' | 'alert' | 'warning' | 'error'

interface DialogOptions {
  title?: string
  message: string
  type?: DialogType
  confirmText?: string
  cancelText?: string
}

interface DialogContextType {
  confirm: (options: DialogOptions) => Promise<boolean>
  alert: (options: DialogOptions) => Promise<void>
  warning: (options: DialogOptions) => Promise<void>
  error: (options: DialogOptions) => Promise<void>
}

const DialogContext = createContext<DialogContextType | null>(null)

export function DialogProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [dialogOptions, setDialogOptions] = useState<DialogOptions | null>(null)
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null)

  const confirm = useCallback((options: DialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogOptions({ ...options, type: options.type || 'confirm' })
      setIsOpen(true)
      setResolver(() => resolve)
    })
  }, [])

  const alert = useCallback((options: DialogOptions): Promise<void> => {
    return new Promise((resolve) => {
      setDialogOptions({ ...options, type: 'alert' })
      setIsOpen(true)
      setResolver(() => () => resolve())
    })
  }, [])

  const warning = useCallback((options: DialogOptions): Promise<void> => {
    return new Promise((resolve) => {
      setDialogOptions({ ...options, type: 'warning' })
      setIsOpen(true)
      setResolver(() => () => resolve())
    })
  }, [])

  const error = useCallback((options: DialogOptions): Promise<void> => {
    return new Promise((resolve) => {
      setDialogOptions({ ...options, type: 'error' })
      setIsOpen(true)
      setResolver(() => () => resolve())
    })
  }, [])

  const handleConfirm = useCallback(() => {
    if (resolver) {
      resolver(true)
    }
    setIsOpen(false)
    setDialogOptions(null)
    setResolver(null)
  }, [resolver])

  const handleCancel = useCallback(() => {
    if (resolver) {
      resolver(false)
    }
    setIsOpen(false)
    setDialogOptions(null)
    setResolver(null)
  }, [resolver])

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleConfirm()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleCancel()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleConfirm, handleCancel])

  const getDialogStyle = () => {
    if (!dialogOptions) return {}
    
    switch (dialogOptions.type) {
      case 'error':
        return {
          titleClass: 'text-destructive',
          confirmClass: 'bg-destructive text-white hover:bg-destructive/90'
        }
      case 'warning':
        return {
          titleClass: 'text-amber-600 dark:text-amber-500',
          confirmClass: 'bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600'
        }
      default:
        return {
          titleClass: '',
          confirmClass: ''
        }
    }
  }

  const style = getDialogStyle()
  const isConfirmDialog = dialogOptions?.type === 'confirm' || dialogOptions?.type === 'warning' || dialogOptions?.type === 'error'

  return (
    <DialogContext.Provider value={{ confirm, alert, warning, error }}>
      {children}
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={style.titleClass}>
              {dialogOptions?.title || (
                dialogOptions?.type === 'error' ? t('error') :
                dialogOptions?.type === 'warning' ? t('warning') :
                dialogOptions?.type === 'alert' ? t('notice') :
                t('confirm')
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dialogOptions?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {isConfirmDialog && (
              <AlertDialogCancel onClick={handleCancel}>
                {dialogOptions?.cancelText || t('cancel')}
              </AlertDialogCancel>
            )}
            <AlertDialogAction onClick={handleConfirm} className={style.confirmClass}>
              {dialogOptions?.confirmText || (isConfirmDialog ? t('confirm') : t('ok'))}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContext.Provider>
  )
}

export function useDialog() {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider')
  }
  return context
}
